import posthog from 'posthog-js';

/**
 * Canonical event names. Use these constants when calling track* functions
 * so a typo is a build error rather than a silently miss-bucketed event.
 */
export const EVENTS = Object.freeze({
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGGED_OUT: 'user_logged_out',
  SHIPMENT_BOOKED: 'shipment_booked',
  SHIPMENT_EXPOSED: 'shipment_exposed',
  CARRIER_ASSIGNED: 'carrier_assigned',
  DRIVER_ASSIGNED: 'driver_assigned',
  STOP_COMPLETED: 'stop_completed',
  PICKUP_COMPLETED: 'pickup_completed',
  DELIVERY_COMPLETED: 'delivery_completed',
  ORDER_BOOKED: 'order_booked',
  QUOTE_CREATED: 'quote_created',
  INVOICE_CREATED: 'invoice_created',
  INVOICE_REGENERATED: 'invoice_regenerated',
  PAYOUT_REFRESHED: 'payout_refreshed',
  ACCESSORIAL_CREATED: 'accessorial_created',
  ACCESSORIAL_REMOVED: 'accessorial_removed',
  ACCESSORIAL_APPROVED: 'accessorial_approved',
  ACCESSORIAL_DECLINED: 'accessorial_declined',
  BILLING_STATUS_OVERRIDE: 'billing_status_override',
  ORDER_BILLING_STATUS_OVERRIDE: 'order_billing_status_override',
});

const posthogDisabled = `${POSTHOG_DISABLED}` === 'true';

/**
 * Sends an event to PostHog. No-ops if PostHog is disabled for this
 * environment or if the SDK isn't initialized yet (checked via the public
 * `config.token` and the internal `__loaded` flag).
 */
function capture(name, properties) {
  if (posthogDisabled) return;
  if (!posthog.config?.token && !posthog.__loaded) return;
  posthog.capture(name, properties);
}

/** Normalizes a value to an array. null/undefined → []; non-array → [value]. */
function toArray(value) {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

/**
 * Extracts the canonical shipment shape for an analytics payload.
 * Accepts a populated shipment, a string id, or null/undefined.
 * Nested shipper/carrier/driver/order/market are flattened to one level deep.
 */
function shipmentProps(shipment) {
  if (!shipment) return {};
  if (typeof shipment === 'string') return { _id: shipment };
  return {
    _id: shipment._id ?? shipment.id,
    created_at: shipment.created_at,
    shipment_id: shipment.shipment_id,
    shipment_type: shipment.shipment_type,
    leg_management_version: shipment.leg_management_version,
    billing_status: shipment.billing_status,
    order: orderProps(shipment.order),
    market: marketProps(shipment.market),
    shipper: shipperProps(shipment.shipper),
    carrier: carrierProps(shipment.carrier),
    driver: driverProps(shipment.driver),
  };
}

/**
 * Extracts the canonical order shape.
 * `_id` falls back to `id` and finally `order_id` to support bulk-override op
 * payloads where `order_id` is the mongo id rather than the friendly id.
 */
function orderProps(order) {
  if (!order) return {};
  if (typeof order === 'string') return { _id: order };
  return {
    _id: order._id ?? order.id ?? order.order_id,
    order_id: order.order_id,
    created_at: order.created_at,
    billing_status: order.billing_status,
    market: marketProps(order.market),
    shipper: shipperProps(order.shipper),
    carrier: carrierProps(order.carrier),
  };
}

/**
 * Extracts the canonical accessorial shape. Order context is read through
 * `accessorial.shipment.order` (via the nested shipment) rather than at the
 * top level — callers don't need an explicit `order` field.
 */
function accessorialProps(accessorial) {
  if (!accessorial) return {};
  // Shorthaul: a string slug like "airport_pickup".
  if (typeof accessorial === 'string') {
    return { _id: accessorial, accessorial_type: accessorial };
  }
  // Shorthaul subdocs use `key` as the identifier and `value` for amount;
  // drayage uses `_id` with `shipper_cost`/`carrier_cost`.
  return {
    _id: accessorial._id ?? accessorial.id ?? accessorial.key,
    created_at: accessorial.created_at,
    accessorial_type: accessorial.accessorial_type ?? accessorial.key,
    shipper_status: accessorial.shipper_status,
    carrier_status: accessorial.carrier_status,
    shipper_cost: accessorial.shipper_cost,
    carrier_cost: accessorial.carrier_cost,
    value: accessorial.value,
    shipment: shipmentProps(accessorial.shipment),
  };
}

/** Extracts the canonical stop shape. Location can arrive as object, array, or id. */
function stopProps(stop) {
  if (!stop) return {};
  return {
    _id: stop._id,
    action: stop.action,
    sequence: stop.sequence,
    empty: stop.empty,
    offsite: stop.offsite,
    location: locationProps(stop.location),
    window_start: stop.window_start,
    window_end: stop.window_end,
  };
}

/** Extracts the canonical user shape (the actor on identity events). */
function userProps(user) {
  if (!user) return {};
  if (typeof user === 'string') return { _id: user };
  return {
    _id: user._id,
    email: user.email,
    name: user.name,
    user_type: user._type,
    roles: user.roles ?? [],
    tags: user.tags ?? [],
  };
}

/** Extracts the canonical quote shape. */
function quoteProps(quote) {
  if (!quote) return {};
  if (typeof quote === 'string') return { _id: quote };
  return {
    _id: quote._id ?? quote.id,
    quote_id: quote.quote_id,
    cost: quote.cost,
    created_by: quote.created_by,
    shipment_type: quote.shipment_type,
    shipper: shipperProps(quote.shipper),
    market: marketProps(quote.market),
  };
}

/** Extracts the canonical payout shape. */
function payoutProps(payout) {
  if (!payout) return {};
  if (typeof payout === 'string') return { _id: payout };
  return {
    _id: payout._id ?? payout.id,
    payout_number: payout.number,
    amount: payout.total,
    status: payout.status,
    carrier: carrierProps(payout.carrier),
  };
}

/** Extracts the canonical invoice shape. */
function invoiceProps(invoice) {
  if (!invoice) return {};
  if (typeof invoice === 'string') return { _id: invoice };
  return {
    _id: invoice._id ?? invoice.id,
    invoice_number: invoice.number,
    amount: invoice.total,
    created_at: invoice.date_created,
    shipper: shipperProps(invoice.shipper),
  };
}

/**
 * Extracts the canonical shipper shape.
 * Note: shippers are companies; the backend field for the display name is
 * `company_name` (not `name`) — do not "fix" this to `shipper.name`.
 */
function shipperProps(shipper) {
  if (!shipper) return {};
  if (typeof shipper === 'string') return { _id: shipper };
  return {
    _id: shipper._id,
    name: shipper.company_name,
    master_account: masterAccountProps(shipper.master_account),
  };
}

/** Extracts the canonical carrier shape. */
function carrierProps(carrier) {
  if (!carrier) return {};
  if (typeof carrier === 'string') return { _id: carrier };
  return {
    _id: carrier._id,
    name: carrier.name,
  };
}

/** Extracts the canonical market shape. */
function marketProps(market) {
  if (!market) return {};
  if (typeof market === 'string') return { _id: market };
  return {
    _id: market._id ?? market.id,
    name: market.name,
    code: market.code,
  };
}

/** Extracts the canonical location shape. Unwraps single-element arrays. */
function locationProps(location) {
  if (!location) return {};
  if (Array.isArray(location)) return locationProps(location[0]);
  if (typeof location === 'string') return { _id: location };
  return {
    _id: location._id,
    name: location.name,
    address: location.address,
    city: location.city,
    state: location.state,
    lat: location.lat,
    lng: location.lng,
    market: marketProps(location.market),
  };
}

/** Extracts the canonical master account shape. */
function masterAccountProps(account) {
  if (!account) return {};
  if (typeof account === 'string') return { _id: account };
  return {
    _id: account._id ?? account.id,
    name: account.name,
  };
}

/**
 * Extracts the canonical driver shape.
 * Builds a display name from first_name + last_name, defaulting to "Unknown".
 */
function driverProps(driver) {
  if (!driver) return {};
  if (typeof driver === 'string') return { _id: driver };
  const fullName = [driver.first_name, driver.last_name].filter(Boolean).join(' ');
  return {
    _id: driver._id,
    name: fullName || 'Unknown',
  };
}

/**
 * Reserved keys recognized in the `extra` arg of every track* function.
 * Pass a raw entity (or string id, or array) and it gets nested under the
 * same key after running through its extractor.
 *
 * Example: trackShipmentEvent('carrier_assigned', shipments, { carrier })
 * yields { ...shipmentProps, carrier: carrierProps(carrier) }.
 */
const CONTEXT_EXTRACTORS = {
  shipment: shipmentProps,
  shipper: shipperProps,
  carrier: carrierProps,
  driver: driverProps,
  market: marketProps,
  stop: stopProps,
  order: orderProps,
  accessorial: accessorialProps,
  quote: quoteProps,
  payout: payoutProps,
  invoice: invoiceProps,
  user: userProps,
  location: locationProps,
  master_account: masterAccountProps,
};

/** Runs reserved keys in `extra` through their extractor; passes other keys through. */
function expandExtra(extra) {
  if (!extra) return {};
  const result = {};
  for (const [key, value] of Object.entries(extra)) {
    const extractor = CONTEXT_EXTRACTORS[key];
    result[key] = extractor ? extractor(value) : value;
  }
  return result;
}

/**
 * Wraps a track function so any error in extraction or capture is swallowed
 * and logged. Tracking must never break the caller.
 */
function safe(fn) {
  return (...args) => {
    try {
      return fn(...args);
    } catch (err) {
      console.error('[analytics] tracking failed', args[0], err);
    }
  };
}

/**
 * Fires `EVENTS.USER_LOGGED_IN` / `EVENTS.USER_LOGGED_OUT`.
 * `user` is the actor; pass null/undefined for events that have no actor context.
 * `extra` accepts any of the keys in `CONTEXT_EXTRACTORS` (auto-expanded) plus
 * arbitrary scalar/flat fields passed through as-is.
 */
export const trackUserEvent = safe((name, user, extra = {}) => {
  const expanded = expandExtra(extra);
  if (user == null) {
    capture(name, expanded);
    return;
  }
  toArray(user).forEach((u) =>
    capture(name, { ...userProps(u), ...expanded }),
  );
});

/**
 * Fires a shipment-centric event (`EVENTS.SHIPMENT_BOOKED`, `CARRIER_ASSIGNED`,
 * `DRIVER_ASSIGNED`, `BILLING_STATUS_OVERRIDE`, etc.).
 * `shipment` may be a single shipment, an array of shipments, or a string id.
 * `extra` accepts any of the keys in `CONTEXT_EXTRACTORS` (auto-expanded).
 */
export const trackShipmentEvent = safe((name, shipment, extra = {}) => {
  const expanded = expandExtra(extra);
  toArray(shipment).forEach((s) =>
    capture(name, { ...shipmentProps(s), ...expanded }),
  );
});

/**
 * Fires an order-centric event (`EVENTS.ORDER_BOOKED`,
 * `ORDER_BILLING_STATUS_OVERRIDE`).
 */
export const trackOrderEvent = safe((name, order, extra = {}) => {
  const expanded = expandExtra(extra);
  toArray(order).forEach((o) =>
    capture(name, { ...orderProps(o), ...expanded }),
  );
});

/** Fires a quote-centric event (`EVENTS.QUOTE_CREATED`). */
export const trackQuoteEvent = safe((name, quote, extra = {}) => {
  const expanded = expandExtra(extra);
  toArray(quote).forEach((q) =>
    capture(name, { ...quoteProps(q), ...expanded }),
  );
});

/** Fires a payout-centric event (`EVENTS.PAYOUT_REFRESHED`). */
export const trackPayoutEvent = safe((name, payout, extra = {}) => {
  const expanded = expandExtra(extra);
  toArray(payout).forEach((p) =>
    capture(name, { ...payoutProps(p), ...expanded }),
  );
});

/** Fires an invoice-centric event (`EVENTS.INVOICE_CREATED`, `INVOICE_REGENERATED`). */
export const trackInvoiceEvent = safe((name, invoice, extra = {}) => {
  const expanded = expandExtra(extra);
  toArray(invoice).forEach((inv) =>
    capture(name, { ...invoiceProps(inv), ...expanded }),
  );
});

/**
 * Fires a stop-centric event (`EVENTS.STOP_COMPLETED`, `PICKUP_COMPLETED`,
 * `DELIVERY_COMPLETED`). Shipment is required context and nested under `shipment`.
 */
export const trackStopEvent = safe((name, shipment, stop, extra = {}) => {
  const expanded = expandExtra(extra);
  const ship = shipmentProps(shipment);
  toArray(stop).forEach((s) =>
    capture(name, {
      ...stopProps(s),
      shipment: ship,
      ...expanded,
    }),
  );
});

/**
 * Fires an accessorial-centric event (`EVENTS.ACCESSORIAL_CREATED`,
 * `ACCESSORIAL_APPROVED`, `ACCESSORIAL_DECLINED`). Each accessorial should
 * carry its `.shipment` field populated for full context.
 */
export const trackAccessorialEvent = safe((name, accessorial, extra = {}) => {
  const expanded = expandExtra(extra);
  toArray(accessorial).forEach((a) =>
    capture(name, { ...accessorialProps(a), ...expanded }),
  );
});
